import { ReactNode } from "react";
import {
  Button,
  InputAdornment,
  SxProps,
  TextField,
  Theme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { commonStyles } from "../commonStyles";

// Shared manage-page filter controls (platform UI/UX polish, Phase A). The
// popup Menus stay page-owned — these are the row layout, search field and
// trigger/clear buttons only. Every default is a verbatim copy of what
// ManageMembers renders today; the other manage pages adopt in Phase B by
// overriding the escape-hatch props.

const cpaGold = commonStyles.cpaGold;
const cpaGoldDark = commonStyles.cpaGoldDark;

export const filterControlBorderColor = "#8F969E";

// Verbatim ManageMembers search-field sx.
export const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "6px",
    height: 56,
    "& fieldset": {
      borderColor: filterControlBorderColor,
    },
    "&:hover fieldset": {
      borderColor: "#6B7280",
    },
    "&.Mui-focused fieldset": {
      borderColor: cpaGold,
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-input": {
    fontSize: 15,
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#8F969E",
    opacity: 1,
  },
};

// Verbatim ManageMembers filter-menu button sx.
export const filterButtonSx = {
  backgroundColor: "white",
  borderColor: filterControlBorderColor,
  borderRadius: "6px",
  color: "#6B7280",
  fontSize: 15,
  fontWeight: 700,
  height: 56,
  justifyContent: "flex-start",
  minWidth: 0,
  px: 2,
  textTransform: "none",
  whiteSpace: "nowrap",
  width: "100%",
  "& .MuiButton-startIcon": {
    color: "#8F969E",
    mr: 1.25,
  },
  "& .MuiButton-endIcon": {
    ml: "auto",
    pl: 1,
  },
  "&:hover": {
    backgroundColor: "white",
    borderColor: "#6B7280",
  },
};

// Gold-bordered variant signalling the filter differs from the default
// selection ("indicates active filter").
export const activeFilterButtonSx = {
  ...filterButtonSx,
  borderColor: cpaGold,
  color: "#111827",
  "& .MuiButton-startIcon": {
    color: cpaGold,
    mr: 1.25,
  },
  "&:hover": {
    backgroundColor: "white",
    borderColor: cpaGoldDark,
  },
};

// Claude Design "Clear All Filter" button (every CMP filter bar): #F21B1B,
// white 14px bold text, 8px radius. Compact and default are the same button.
export const clearFiltersButtonCompactSx = {
  backgroundColor: "#F21B1B",
  borderRadius: "8px",
  boxShadow: "none",
  color: "#FFFFFF",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 14,
  fontWeight: 700,
  height: 43,
  letterSpacing: "normal",
  lineHeight: "19px",
  minHeight: 43,
  padding: "12px 22px",
  textTransform: "none",
  whiteSpace: "nowrap",
  "&:hover": { backgroundColor: "#D01414", boxShadow: "none", color: "#FFFFFF" },
} as const;

export const clearFiltersButtonSx = clearFiltersButtonCompactSx;

// Verbatim ManageMembers gold count pill.
export const filterCountBadgeClassName =
  "ml-2 inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-cpaGold px-1.5 text-xs font-extrabold text-white";

export type FilterBarProps = {
  children: ReactNode;
  className?: string;
};

export const FilterBar = ({
  children,
  className = "mt-5 flex flex-col gap-3 xl:flex-row xl:items-center",
}: FilterBarProps) => <div className={className}>{children}</div>;

export type FilterSearchFieldProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel?: string;
  // applied to the TextField itself
  className?: string;
  fullWidth?: boolean;
  adornmentIcon?: ReactNode;
  fieldSx?: SxProps<Theme>;
};

export const FilterSearchField = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
  className = "min-w-[280px] xl:flex-[1_1_560px]",
  fullWidth = true,
  adornmentIcon,
  fieldSx = filterFieldSx,
}: FilterSearchFieldProps) => (
  <TextField
    value={value}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    inputProps={{ "aria-label": ariaLabel ?? placeholder }}
    fullWidth={fullWidth}
    className={className}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          {adornmentIcon ?? <SearchIcon sx={{ color: "#8F969E" }} />}
        </InputAdornment>
      ),
    }}
    sx={fieldSx}
  />
);

export type FilterMenuButtonProps = {
  // static ("Filter by Member Status") or dynamic (Invoices date-range label)
  label: ReactNode;
  // page stores the anchor + renders its own <Menu>
  onOpen: (anchorEl: HTMLElement) => void;
  // true → activeFilterButtonSx (gold border), else filterButtonSx
  active?: boolean;
  // Events/Invoices date buttons pass <CalendarTodayOutlinedIcon />
  startIcon?: ReactNode;
  // non-null → gold count pill; Members passes
  // statusFilterIsActive ? selectedStatuses.length : null
  badgeCount?: number | null;
  badgeAriaLabel?: string;
  // Members wraps in <div className="xl:w-[260px]">; undefined → no wrapper
  wrapperClassName?: string;
  // merged over the chosen base sx (Events Phase B removes width:"100%")
  sx?: SxProps<Theme>;
};

export const FilterMenuButton = ({
  label,
  onOpen,
  active = false,
  startIcon,
  badgeCount = null,
  badgeAriaLabel,
  wrapperClassName,
  sx,
}: FilterMenuButtonProps) => {
  const baseSx = active ? activeFilterButtonSx : filterButtonSx;
  // Plain-text labels get the design chevron here. Custom label nodes
  // (Event Sales, Manage Invoices) already render their own chevron.
  const plainLabel = typeof label === "string";
  const button = (
    <Button
      type="button"
      variant="outlined"
      startIcon={startIcon ?? <TuneOutlinedIcon />}
      // Claude Design SearchableSelect trigger: a chevron at the right edge.
      endIcon={plainLabel ? <KeyboardArrowDownIcon sx={{ color: "#0A0A0A" }} /> : undefined}
      onClick={(event) => onOpen(event.currentTarget)}
      sx={
        sx
          ? ([baseSx, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>)
          : baseSx
      }
    >
      {plainLabel ? (
        <span className="min-w-0 overflow-hidden text-ellipsis">{label}</span>
      ) : (
        label
      )}
      {badgeCount !== null && badgeCount !== undefined && (
        <span aria-label={badgeAriaLabel} className={filterCountBadgeClassName}>
          {badgeCount}
        </span>
      )}
    </Button>
  );

  return wrapperClassName ? (
    <div className={wrapperClassName}>{button}</div>
  ) : (
    button
  );
};

export type ClearFiltersButtonProps = {
  onClick: () => void;
  // Members: !hasActiveFilters; Events/Emails never pass (always enabled)
  disabled?: boolean;
  // Claude Design (4) uses the singular "Clear All Filter" in filter bars;
  // the plural stays only where the design shows it (Emails audience filters
  // and table empty states).
  label?: string;
  // Members: "xl:w-[220px]"
  wrapperClassName?: string;
  sx?: SxProps<Theme>;
};

export const ClearFiltersButton = ({
  onClick,
  disabled,
  label = "Clear All Filter",
  wrapperClassName,
  sx = clearFiltersButtonSx,
}: ClearFiltersButtonProps) => {
  const button = (
    <Button
      type="button"
      variant="contained"
      onClick={onClick}
      disabled={disabled}
      sx={sx}
    >
      {label}
    </Button>
  );

  return wrapperClassName ? (
    <div className={wrapperClassName}>{button}</div>
  ) : (
    button
  );
};
